import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Shield, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { apiRequest } from "../lib/utils";

/**
 * Componente de diálogo para verificar a senha do administrador antes de exclusões.
 * 
 * Props:
 * - open: boolean - Controla se o diálogo está aberto
 * - onOpenChange: (open: boolean) => void - Callback quando o estado muda
 * - onConfirm: () => void - Callback executado quando a senha é verificada com sucesso
 * - title: string - Título do diálogo (opcional)
 * - description: string - Descrição do que será excluído (opcional)
 * - itemName: string - Nome do item sendo excluído (opcional)
 */
export const AdminPasswordDialog = ({
  open,
  onOpenChange,
  onConfirm,
  title = "Confirmação de Exclusão",
  description = "Esta ação não pode ser desfeita. Digite a senha do administrador para confirmar.",
  itemName = "",
}) => {
  const [senha, setSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    if (!senha.trim()) {
      setError("Digite a senha do administrador");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await apiRequest("/admin/verificar-senha", {
        method: "POST",
        body: JSON.stringify({ senha_admin: senha }),
      });

      // Senha verificada com sucesso
      setSenha("");
      setError("");
      onConfirm();
      onOpenChange(false);
    } catch (err) {
      setError(err.message || "Senha incorreta. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSenha("");
    setError("");
    onOpenChange(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) {
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="admin-password-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-red-100">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-lg">{title}</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Aviso */}
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Atenção!</p>
              <p>{description}</p>
              {itemName && (
                <p className="mt-1 font-semibold">Item: {itemName}</p>
              )}
            </div>
          </div>

          {/* Campo de senha */}
          <div className="space-y-2">
            <Label htmlFor="admin-password">Senha do Administrador</Label>
            <div className="relative">
              <Input
                id="admin-password"
                type={showPassword ? "text" : "password"}
                value={senha}
                onChange={(e) => {
                  setSenha(e.target.value);
                  setError("");
                }}
                onKeyDown={handleKeyDown}
                placeholder="Digite a senha do admin"
                className={error ? "border-red-500" : ""}
                data-testid="admin-password-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {error && (
              <p className="text-sm text-red-600" data-testid="admin-password-error">
                {error}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            data-testid="admin-password-cancel"
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={loading || !senha.trim()}
            data-testid="admin-password-confirm"
          >
            {loading ? "Verificando..." : "Confirmar Exclusão"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPasswordDialog;
